<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 text-white overflow-hidden py-20 md:py-32">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-blue-400 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-indigo-400 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-blue-300">
            <i class="bx bx-transfer mr-2"></i>Digital Transformation
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          Cloud Migration <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Services</span>
        </h1>
        
        <p class="text-xl text-blue-100 mb-8 max-w-2xl mx-auto lg:mx-0">
          Seamless cloud migration with minimal downtime and maximum efficiency. Transform your business operations with our comprehensive migration strategy, expert planning, and proven methodologies for successful cloud adoption.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
          <a routerLink="/contact" class="bg-transparent border-2 border-cyan-400 text-cyan-300 hover:bg-cyan-400/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-transfer mr-2"></i>
            Migration Assessment
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto lg:mx-0">
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">Zero</div>
            <div class="text-sm text-blue-200">Data Loss</div>
          </div>
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">Minimal</div>
            <div class="text-sm text-blue-200">Downtime</div>
          </div>
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">24/7</div>
            <div class="text-sm text-blue-200">Support</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/cloud-migration-hero.png" alt="Cloud Migration Services" class="w-full h-auto object-cover">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-server text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Server</div>
              <div class="text-xs">Migration</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-data text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Data</div>
              <div class="text-xs">Transfer</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- 4-Phase Migration Process Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Proven Methodology
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        4-Phase Migration <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-500">Process</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Our structured migration methodology ensures successful cloud adoption with minimal risk and maximum efficiency through careful planning and execution.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
      <!-- Phase 1: Assessment -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-cyan-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
            <span class="text-2xl font-bold text-white">1</span>
          </div>
          <span class="bg-cyan-100 text-cyan-800 text-xs font-bold px-3 py-1 rounded-full">Assessment</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Discovery & Assessment</h3>
        <p class="text-slate-300 mb-4">
          Comprehensive evaluation of existing infrastructure, applications, and data dependencies to create the optimal migration strategy.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Infrastructure audit</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Application assessment</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Dependency mapping</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Cost analysis</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Risk evaluation</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Performance baseline</span>
          </li>
        </ul>
        <div class="grid grid-cols-1 gap-3 pt-4 border-t border-slate-700/60">
          <div class="flex items-center text-sm text-slate-300">
            <i class="bx bx-time text-cyan-600 mr-2"></i>
            <span>Duration: 1-2 weeks</span>
          </div>
          <div class="flex items-center text-sm text-slate-300">
            <i class="bx bx-group text-cyan-600 mr-2"></i>
            <span>Team: 3-5 specialists</span>
          </div>
        </div>
      </div>

      <!-- Phase 2: Planning -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-blue-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <span class="text-2xl font-bold text-white">2</span>
          </div>
          <span class="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">Planning</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Strategy & Planning</h3>
        <p class="text-slate-300 mb-4">
          Detailed migration roadmap with timeline, resource allocation, and risk mitigation strategies for successful execution.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Migration roadmap</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Timeline planning</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Resource allocation</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Risk mitigation</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Security framework</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Compliance checklist</span>
          </li>
        </ul>
        <div class="grid grid-cols-1 gap-3 pt-4 border-t border-slate-700/60">
          <div class="flex items-center text-sm text-slate-300">
            <i class="bx bx-time text-cyan-600 mr-2"></i>
            <span>Duration: 2-3 weeks</span>
          </div>
          <div class="flex items-center text-sm text-slate-300">
            <i class="bx bx-group text-cyan-600 mr-2"></i>
            <span>Team: 4-6 specialists</span>
          </div>
        </div>
      </div>

      <!-- Phase 3: Execution -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-indigo-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <span class="text-2xl font-bold text-white">3</span>
          </div>
          <span class="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full">Execution</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Migration Execution</h3>
        <p class="text-slate-300 mb-4">
          Careful implementation of the migration plan with continuous monitoring and minimal business disruption throughout the process.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Phased migration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Real-time monitoring</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Data validation</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Testing & verification</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Rollback procedures</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Performance tuning</span>
          </li>
        </ul>
        <div class="grid grid-cols-1 gap-3 pt-4 border-t border-slate-700/60">
          <div class="flex items-center text-sm text-slate-300">
            <i class="bx bx-time text-cyan-600 mr-2"></i>
            <span>Duration: 3-8 weeks</span>
          </div>
          <div class="flex items-center text-sm text-slate-300">
            <i class="bx bx-group text-cyan-600 mr-2"></i>
            <span>Team: 5-10 specialists</span>
          </div>
        </div>
      </div>

      <!-- Phase 4: Optimization -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-purple-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center">
            <span class="text-2xl font-bold text-white">4</span>
          </div>
          <span class="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1 rounded-full">Optimization</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Post-Migration Optimization</h3>
        <p class="text-slate-300 mb-4">
          Performance tuning, cost optimization, and ongoing support to maximize the benefits of your cloud investment.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Performance tuning</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Cost optimization</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Security hardening</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Ongoing support</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Automation setup</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Knowledge transfer</span>
          </li>
        </ul>
        <div class="grid grid-cols-1 gap-3 pt-4 border-t border-slate-700/60">
          <div class="flex items-center text-sm text-slate-300">
            <i class="bx bx-time text-cyan-600 mr-2"></i>
            <span>Duration: 4-6 weeks</span>
          </div>
          <div class="flex items-center text-sm text-slate-300">
            <i class="bx bx-group text-cyan-600 mr-2"></i>
            <span>Team: 3-5 specialists</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Migration Capabilities Section -->
    <section class="py-20 bg-slate-950">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <div class="inline-block cm-badge px-4 py-1 rounded-full text-sm font-semibold mb-4">
            Capabilities & Tooling
          </div>
          <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
            Enterprise <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-500">Cloud Migration</span> Stack
          </h2>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto">
            Proven methodologies across assessment, planning, execution, and optimization with best-in-class tools.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="cm-card cm-accent-1">
            <div class="cm-card-header">
              <div class="cm-card-icon"><i class="bx bx-analyse"></i></div>
              <span class="cm-card-badge">Discovery</span>
            </div>
            <div class="cm-card-title">Assessment</div>
            <p class="cm-card-desc">Inventory analysis, dependency mapping, total cost evaluation.</p>
            <div class="cm-card-tags"><span>Inventory</span><span>Dependencies</span><span>TCO</span></div>
          </div>
          <div class="cm-card cm-accent-2">
            <div class="cm-card-header">
              <div class="cm-card-icon"><i class="bx bx-map"></i></div>
              <span class="cm-card-badge">Planning</span>
            </div>
            <div class="cm-card-title">Blueprint</div>
            <p class="cm-card-desc">Roadmap, sequencing, risk mitigation, rollback strategy.</p>
            <div class="cm-card-tags"><span>Roadmap</span><span>Risk</span><span>Rollback</span></div>
          </div>
          <div class="cm-card cm-accent-3">
            <div class="cm-card-header">
              <div class="cm-card-icon"><i class="bx bx-transfer-alt"></i></div>
              <span class="cm-card-badge">Execution</span>
            </div>
            <div class="cm-card-title">Migration</div>
            <p class="cm-card-desc">Phased moves, data sync, validation, controlled cutover.</p>
            <div class="cm-card-tags"><span>Phased</span><span>Sync</span><span>Cutover</span></div>
          </div>
          <div class="cm-card cm-accent-4">
            <div class="cm-card-header">
              <div class="cm-card-icon"><i class="bx bx-cog"></i></div>
              <span class="cm-card-badge">Ops</span>
            </div>
            <div class="cm-card-title">Optimization</div>
            <p class="cm-card-desc">Performance tuning, autoscaling, cost governance.</p>
            <div class="cm-card-tags"><span>Tuning</span><span>Autoscale</span><span>FinOps</span></div>
          </div>
          <div class="cm-card cm-accent-5">
            <div class="cm-card-header">
              <div class="cm-card-icon"><i class="bx bx-shield-quarter"></i></div>
              <span class="cm-card-badge">Security</span>
            </div>
            <div class="cm-card-title">Protection</div>
            <p class="cm-card-desc">Encryption, IAM hardening, continuous compliance.</p>
            <div class="cm-card-tags"><span>Encryption</span><span>IAM</span><span>Compliance</span></div>
          </div>
          <div class="cm-card cm-accent-6">
            <div class="cm-card-header">
              <div class="cm-card-icon"><i class="bx bx-network-chart"></i></div>
              <span class="cm-card-badge">Network</span>
            </div>
            <div class="cm-card-title">Connectivity</div>
            <p class="cm-card-desc">Hybrid links, VPN/DirectConnect, secure routing.</p>
            <div class="cm-card-tags"><span>Hybrid</span><span>VPN</span><span>Routing</span></div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Key Benefits Section -->
    <div class="text-center mb-12">
      <h3 class="text-2xl md:text-3xl font-bold text-white mb-4">Key Benefits of Our Methodology</h3>
      <p class="text-lg text-slate-300 max-w-2xl mx-auto">Why our proven approach delivers exceptional results</p>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <!-- Benefit 1 -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 text-center border border-blue-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-shield-quarter text-3xl text-white"></i>
        </div>
        <h4 class="text-xl font-bold text-white mb-3">Zero Downtime</h4>
        <p class="text-slate-300">Minimize business disruption with our phased approach</p>
      </div>
      
      <!-- Benefit 2 -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 text-center border border-cyan-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-lock text-3xl text-white"></i>
        </div>
        <h4 class="text-xl font-bold text-white mb-3">Secure Migration</h4>
        <p class="text-slate-300">Enterprise-grade security throughout the process</p>
      </div>
      
      <!-- Benefit 3 -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 text-center border border-purple-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-line-chart text-3xl text-white"></i>
        </div>
        <h4 class="text-xl font-bold text-white mb-3">Cost Optimization</h4>
        <p class="text-slate-300">Reduce cloud costs by up to 40% post-migration</p>
      </div>
      
      <!-- Benefit 4 -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 text-center border border-emerald-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-support text-3xl text-white"></i>
        </div>
        <h4 class="text-xl font-bold text-white mb-3">24/7 Support</h4>
        <p class="text-slate-300">Round-the-clock assistance during and after migration</p>
      </div>
    </div>
  </div>
</section>

<!-- Cloud Migration Services Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Migration Solutions
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Cloud Migration <span class="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-blue-500">Services</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Comprehensive migration services for all types of workloads, applications, and infrastructure components with expert guidance and support.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Lift & Shift Migration -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-transfer-alt text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-blue-500/20 text-blue-200 text-xs font-bold px-3 py-1 rounded-full border border-blue-500/40">Core</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Lift & Shift Migration</h3>
        <p class="text-slate-300 mb-4">
          Quick and efficient migration of existing applications and infrastructure to the cloud with minimal modifications and rapid deployment.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Virtual machine migration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Application rehosting</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Database migration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Network configuration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Security group setup</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Performance monitoring</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Migration Time:</div>
            <div class="font-semibold text-white">2-4 weeks</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Downtime:</div>
            <div class="font-semibold text-white">&lt; 4 hours</div>
          </div>
        </div>
      </div>

      <!-- Application Modernization -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-cyan-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-cog text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-cyan-500/20 text-cyan-200 text-xs font-bold px-3 py-1 rounded-full border border-cyan-500/40">Modernization</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Application Modernization</h3>
        <p class="text-slate-300 mb-4">
          Transform legacy applications to cloud-native architectures with microservices, containers, and modern development practices.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Microservices architecture</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Containerization</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">API modernization</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">DevOps integration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">CI/CD pipeline setup</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Cloud-native features</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Project Duration:</div>
            <div class="font-semibold text-white">3-6 months</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Scalability:</div>
            <div class="font-semibold text-white">Auto-scaling</div>
          </div>
        </div>
      </div>

      <!-- Database Migration -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-purple-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-data text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-purple-500/20 text-purple-200 text-xs font-bold px-3 py-1 rounded-full border border-purple-500/40">Data</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Database Migration</h3>
        <p class="text-slate-300 mb-4">
          Secure and efficient database migration with data integrity validation, minimal downtime, and performance optimization.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Schema migration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Data replication</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Performance tuning</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Data validation</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Backup & recovery</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Security configuration</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Data Integrity:</div>
            <div class="font-semibold text-white">100%</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Sync Time:</div>
            <div class="font-semibold text-white">Real-time</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-cyan-600 via-blue-700 to-indigo-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-cyan-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="text-center max-w-4xl mx-auto">
      <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
        Ready to Migrate to the Cloud?
      </h2>
      <p class="text-xl text-blue-100 mb-10">
        Transform your business with our proven cloud migration methodology. Start your digital transformation journey today with our expert migration team and comprehensive support.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-4 justify-center mb-12">
        <a routerLink="/contact" class="bg-slate-800/30 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/30 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl inline-flex items-center justify-center">
          <i class="bx bx-cloud mr-2 text-xl"></i>
          <span class="text-lg">Contact Us for Details</span>
        </a>
        <a routerLink="/contact" class="bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300 inline-flex items-center justify-center">
          <i class="bx bx-support mr-2 text-xl"></i>
          <span class="text-lg">Talk to Migration Expert</span>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/40 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">99.9%</div>
          <div class="text-blue-200 text-sm">Success Rate</div>
        </div>
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/40 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">24/7</div>
          <div class="text-blue-200 text-sm">Support</div>
        </div>
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/40 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">Zero</div>
          <div class="text-blue-200 text-sm">Data Loss</div>
        </div>
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/40 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">Minimal</div>
          <div class="text-blue-200 text-sm">Downtime</div>
        </div>
      </div>
    </div>
  </div>
</section>
